import React, { useState, useCallback, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  BsChevronDown,
  BsChevronRight,
  BsExclamationTriangleFill,
} from "react-icons/bs";
import ClientList from "./ClientList";
import FGEditModal from "./FGEditModal";

const BASE = "https://eps.elkomprintama.com";
const toNum = (n) => {
  const v = Number(n);
  return isNaN(v) ? null : v;
};
const fmt = (n) => {
  const v = toNum(n);
  return v === null ? "—" : v.toLocaleString("en-US");
};
const safeSum = (arr, key) =>
  arr.reduce((a, r) => {
    const v = toNum(r[key]);
    return a + (v ?? 0);
  }, 0);
const hasError = (r) => toNum(r.qty) === null || toNum(r.finish_qty) === null;

const StatCard = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex flex-col gap-1">
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
      {label}
    </p>
    <p className="text-2xl font-bold text-gray-800">{value}</p>
  </div>
);

function ClientReport() {
  const [clientId, setClientId] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [modalRecord, setModalRecord] = useState(null);

  const handleRecordSaved = (fg_id, qty, finish_qty) => {
    setRecords((prev) =>
      prev.map((r) => (r.fg_id === fg_id ? { ...r, qty, finish_qty } : r)),
    );
  };

  const handleSearch = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setSearched(true);
    setExpanded({});
    try {
      const res = await axios.get(`${BASE}/get/api/fin_orders/by-customer`, {
        params: { cust_id: clientId },
      });
      setRecords(res.data.result || []);
    } catch (e) {
      console.error(e);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const grouped = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const key = r.finish_date
        ? format(new Date(r.finish_date), "yyyy-MM")
        : "unknown";
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return Object.entries(map).sort(([a], [b]) => b.localeCompare(a));
  }, [records]);

  const totalJobs = records.length;
  const totalQty = safeSum(records, "finish_qty");
  const errorCount = records.filter(hasError).length;

  const toggleMonth = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const expandAll = () => {
    const all = {};
    grouped.forEach(([k]) => {
      all[k] = true;
    });
    setExpanded(all);
  };
  const collapseAll = () => setExpanded({});

  return (
    <div
      className="flex flex-col gap-4"
      style={{ maxHeight: "calc(100dvh - 80px)" }}
    >
      {modalRecord && (
        <FGEditModal
          record={modalRecord}
          onClose={() => setModalRecord(null)}
          onSaved={handleRecordSaved}
        />
      )}

      {/* Header */}
      <div className="w-full rounded-lg bg-teal-700 flex-shrink-0">
        <h1 className="block uppercase text-white tracking-wide text-4xl text-center font-bold py-2">
          Client Report
        </h1>
      </div>

      {/* Filter */}
      <div className="relative z-20 flex-shrink-0 bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[220px] max-w-sm">
          <ClientList
            onChangeList={(id) => {
              setClientId(id);
              setSearched(false);
              setRecords([]);
            }}
            onInitial={(id) => setClientId(id)}
            showLabel={true}
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={!clientId || loading}
          className="px-6 py-2.5 text-sm font-bold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? "Loading…" : "Search"}
        </button>
      </div>

      {/* Stat cards */}
      {searched && !loading && records.length > 0 && (
        <div className="flex-shrink-0 grid grid-cols-4 gap-4">
          <StatCard label="Months Active" value={grouped.length} />
          <StatCard label="Total Jobsheets" value={fmt(totalJobs)} />
          <StatCard label="Total Qty Produced" value={fmt(totalQty)} />
          <div
            className={`rounded-xl border shadow-sm px-5 py-4 flex flex-col gap-1 ${errorCount > 0 ? "bg-red-50 border-red-200" : "bg-white border-gray-200"}`}
          >
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              Data Errors
            </p>
            <p
              className={`text-2xl font-bold ${errorCount > 0 ? "text-red-600" : "text-gray-800"}`}
            >
              {errorCount}
            </p>
            {errorCount > 0 && (
              <p className="text-xs text-red-400">Double-click rows to fix</p>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-auto bg-white rounded-xl border border-gray-200 shadow-sm">
        {!searched ? (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm italic">
            Select a client and press Search.
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
            Loading…
          </div>
        ) : records.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm italic">
            No completed jobs found for this client.
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-teal-700 text-white uppercase text-xs z-10">
              <tr>
                <th className="px-4 py-3 text-left w-8"></th>
                <th className="px-4 py-3 text-left">FG ID</th>
                <th className="px-4 py-3 text-left">Job Name</th>
                <th className="px-4 py-3 text-left">Size</th>
                <th className="px-4 py-3 text-left">Part No.</th>
                <th className="px-4 py-3 text-left">PO Number</th>
                <th className="px-4 py-3 text-center">Finish Date</th>
                <th className="px-4 py-3 text-right">Order Qty</th>
                <th className="px-4 py-3 text-right">Finish Qty</th>
                <th className="px-4 py-3 text-left">
                  <div className="flex items-center justify-between gap-2">
                    <span>QC</span>
                    <div className="flex gap-1">
                      <button
                        onClick={expandAll}
                        className="text-teal-200 hover:text-white text-[10px] underline normal-case font-normal"
                      >
                        all
                      </button>
                      <span className="opacity-50">·</span>
                      <button
                        onClick={collapseAll}
                        className="text-teal-200 hover:text-white text-[10px] underline normal-case font-normal"
                      >
                        none
                      </button>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {grouped.map(([monthKey, rows]) => {
                const isOpen = !!expanded[monthKey];
                const monthQty = safeSum(rows, "finish_qty");
                const label =
                  monthKey !== "unknown"
                    ? format(new Date(monthKey + "-01"), "MMMM yyyy")
                    : "Unknown Date";
                const monthErrors = rows.filter(hasError).length;

                return (
                  <React.Fragment key={monthKey}>
                    <tr
                      className="bg-teal-50 border-b border-teal-200 cursor-pointer select-none hover:bg-teal-100 transition-colors"
                      onClick={() => toggleMonth(monthKey)}
                    >
                      <td className="px-4 py-2.5 text-teal-600">
                        {isOpen ? (
                          <BsChevronDown size={13} />
                        ) : (
                          <BsChevronRight size={13} />
                        )}
                      </td>
                      <td
                        className="px-4 py-2.5 font-bold text-teal-800"
                        colSpan={5}
                      >
                        {label}
                        <span className="ml-2 text-xs font-normal text-teal-500">
                          {rows.length} job{rows.length !== 1 ? "s" : ""}
                        </span>
                        {monthErrors > 0 && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-red-500">
                            <BsExclamationTriangleFill size={11} />{" "}
                            {monthErrors} error{monthErrors !== 1 ? "s" : ""}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5"></td>
                      <td className="px-4 py-2.5 text-right text-teal-600 text-xs font-semibold">
                        {fmt(safeSum(rows, "qty"))}
                      </td>
                      <td className="px-4 py-2.5 text-right font-bold text-teal-700">
                        {fmt(monthQty)}
                      </td>
                      <td className="px-4 py-2.5"></td>
                    </tr>

                    {isOpen &&
                      rows.map((r, i) => {
                        const err = hasError(r);
                        return (
                          <tr
                            key={r.fg_id}
                            onDoubleClick={() => setModalRecord(r)}
                            className={`border-b transition-colors cursor-default ${
                              err
                                ? "bg-red-50 hover:bg-red-100"
                                : i % 2 === 0
                                  ? "bg-white hover:bg-teal-50"
                                  : "bg-gray-50 hover:bg-teal-50"
                            }`}
                            title={
                              err ? "Double-click to view details and fix" : ""
                            }
                          >
                            <td className="px-4 py-2.5">
                              {err && (
                                <BsExclamationTriangleFill
                                  size={13}
                                  className="text-red-400"
                                />
                              )}
                            </td>
                            <td className="px-4 py-2.5 font-mono text-xs text-gray-400">
                              #{r.fg_id}
                            </td>
                            <td className="px-4 py-2.5 pl-4 font-medium text-gray-800">
                              {r.job_name}
                            </td>
                            <td className="px-4 py-2.5 text-gray-600 text-xs">
                              {r.size}
                            </td>
                            <td className="px-4 py-2.5 text-gray-500 font-mono text-xs">
                              {r.pn_cust}
                            </td>
                            <td className="px-4 py-2.5 text-gray-600 text-xs">
                              {r.po_num || "—"}
                            </td>
                            <td className="px-4 py-2.5 text-center text-gray-600 text-xs">
                              {r.finish_date
                                ? format(new Date(r.finish_date), "dd MMM yyyy")
                                : "—"}
                            </td>
                            <td
                              className={`px-4 py-2.5 text-right text-xs ${toNum(r.qty) === null ? "text-red-500 font-bold" : "text-gray-500"}`}
                            >
                              {fmt(r.qty)}
                            </td>
                            <td
                              className={`px-4 py-2.5 text-right font-bold ${toNum(r.finish_qty) === null ? "text-red-500" : "text-teal-700"}`}
                            >
                              {fmt(r.finish_qty)}
                            </td>
                            <td className="px-4 py-2.5 text-gray-400 text-xs">
                              {r.qc_name || "—"}
                            </td>
                          </tr>
                        );
                      })}
                  </React.Fragment>
                );
              })}

              <tr className="bg-teal-700 text-white font-bold text-sm">
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3" colSpan={6}>
                  GRAND TOTAL — {grouped.length} month
                  {grouped.length !== 1 ? "s" : ""}
                </td>
                <td className="px-4 py-3 text-right">
                  {fmt(safeSum(records, "qty"))}
                </td>
                <td className="px-4 py-3 text-right">{fmt(totalQty)}</td>
                <td className="px-4 py-3"></td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ClientReport;
